import { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@deere/fuel-react";
import { FileDownloadOutlined } from "@mui/icons-material";
import IBFilters from "../calculation/ibFilters";
import {
  IB_GET_DEALER_REPORT,
  IB_GET_DEALER_REPORT_FISMONTH,
} from "../../constants/constant";
import { getDataWAuth } from "../../utils/api-service";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const DealerRateAllocationReport = () => {
  const userData = useSelector((state) => state.userState.userData);
  const [dealerData, setDealerData] = useState([]);
  const [fiscalDateMap, setFiscalDateMap] = useState({});
  const [fiscalMonths, setFiscalMonths] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [defaultYear, setDefaultYear] = useState("");
  const [startFiscalMonth, setStartFiscalMonth] = useState("");
  const [endFiscalMonth, setEndFiscalMonth] = useState("");
  const [isViewReportActive, setIsViewReportActive] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [isDelerDataLoading, setIsDelerDataLoading] = useState(false);
  const [
    isDealerDataWithinFisMonthLoading,
    setIsDealerDataWithinFisMonthLoading,
  ] = useState(false);

  const exportValueFormatter = (value) => {
    if (!value || value === null || value === "" || value === "N/A") return "-";
    else return Number(value).toFixed(2).replace(".", ",");
  };

  const handleExportXLSX = () => {
    const exportHeaders = [
      { label: "Country", key: "country" },
      { label: "Dealer Name", key: "dealerName" },
      { label: "Customer Number", key: "dealerId" },
      ...fiscalMonths.map((monthYear, idx) => ({
        label: formattedFiscalMonths[idx] || "N/A",
        key: monthYear,
      })),
    ];

    const exportRows = dealerData.map((row) =>
      exportHeaders.reduce((acc, col) => {
        if (
          col.key !== "country" &&
          col.key !== "dealerName" &&
          col.key !== "dealerId"
        ) {
          acc[col.label] = exportValueFormatter(row[col.key]);
        } else {
          acc[col.label] = row[col.key];
        }
        return acc;
      }, {}),
    );

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(
      wb,
      `DealerRateReport_${startFiscalMonth}-${defaultYear}_${endFiscalMonth}-${defaultYear}.xlsx`,
    );
  };

  const formattedFiscalMonths = fiscalMonths.map((monthYear) => {
    const startDate = fiscalDateMap[monthYear]?.startDate;
    const endDate = fiscalDateMap[monthYear]?.endDate;

    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("DD MMM YYYY");
      const formattedEndDate = dayjs(endDate).format("DD MMM YYYY");
      return `${formattedStartDate} to ${formattedEndDate}`;
    }
    return "N/A";
  });

  const fetchDealerData = async () => {
    try {
      setIsDelerDataLoading(true);
      const countryCode = userData?.country || "DE";
      const response = await getDataWAuth(
        `${IB_GET_DEALER_REPORT}?countryCodes=${countryCode}`,
      );
      if (response.status === 200) {
        const dealerData = await response.data;

        setDropdownOptions(response.data.listforDropdown || []);
        // Remove JDWIN from countryList
        // const filteredCountryList = (dealerData?.countryList || []).filter(
        //   (c) => !(c.countryName === "JDWIN" && c.countryCode === "JD"),
        // );
        // Sort countryList alphabetically by countryName before setting
        const sortedCountryList = (dealerData?.countryList || [])
          .slice()
          .sort((a, b) => {
            if (a.countryName < b.countryName) return -1;
            if (a.countryName > b.countryName) return 1;
            return 0;
          });
        setCountryOptions(sortedCountryList);
        setSelectedCountries(countryCode);
        // ...existing code for building dealerMap...
        const dealerMap = {};
        dealerData.dealerList.forEach((row) => {
          // For JDWIN countries, use dealerId+currency as key
          const isJDWIN =
            row.countryCode?.countryCode === "JD" || row.countryCode === "JD";
          const key = isJDWIN
            ? `${row.dealerId}_${row.currency}`
            : row.dealerId;
          if (!dealerMap[key]) {
            dealerMap[key] = {
              id: row.generatedId,
              country: row.countryCode?.countryName || "",
              dealerId: row.dealerId,
              dealerName: row.dealerName,
              currency: row.currency || "",
            };
          }
          dealerMap[key][row.fisMonthYear] = row.rate;
        });

        const fiscalMonthsList = response.data.fiscalDateDTOList.map(
          (date) => date.fismonthyear,
        );
        Object.values(dealerMap).forEach((dealer) => {
          fiscalMonthsList.forEach((monthYear) => {
            if (!(monthYear in dealer)) {
              dealer[monthYear] = null;
            }
          });
        });

        // Clean and filter dealer data: remove null months and filter out rows with no rates
        const cleanedDealerData = Object.values(dealerMap)
          .map((dealer) => {
            const cleaned = { ...dealer };
            Object.keys(cleaned).forEach((key) => {
              if (
                /^\d{2}-\d{4}$/.test(key) && // matches "MM-YYYY"
                cleaned[key] === null
              ) {
                delete cleaned[key];
              }
            });
            return cleaned;
          })
          .filter((dealer) => {
            // Keep only if at least one fiscal month key has a non-null, non-undefined, non-empty value
            return Object.keys(dealer).some(
              (key) =>
                /^\d{2}-\d{4}$/.test(key) &&
                dealer[key] !== null &&
                dealer[key] !== undefined &&
                dealer[key] !== "",
            );
          });
        setDealerData(cleanedDealerData);

        const fiscalDateMap = dealerData.fiscalDateDTOList.reduce(
          (acc, date) => {
            acc[date.fismonthyear] = {
              startDate: date.startDate,
              endDate: date.endDate,
            };
            return acc;
          },
          {},
        );

        setFiscalDateMap(fiscalDateMap);

        const fiscalMonths = dealerData.fiscalDateDTOList.map(
          (date) => date.fismonthyear,
        );

        setFiscalMonths(fiscalMonths.sort());

        const newYears = [
          ...new Set(
            response.data.listforDropdown.map(
              (option) => option.fismonthyear.split("-")[1],
            ),
          ),
        ];

        setDefaultYear(newYears[0]);
        setStartFiscalMonth(
          dealerData && dealerData?.fiscalDateDTOList?.length > 0
            ? dealerData.fiscalDateDTOList[2].fismonthyear.split("-")[0]
            : "",
        );
        setEndFiscalMonth(
          dealerData && dealerData?.fiscalDateDTOList?.length > 0
            ? dealerData.fiscalDateDTOList[0].fismonthyear.split("-")[0]
            : "",
        );

        setIsDelerDataLoading(false);
      }
    } catch (error) {
      console.error("Error fetching dealer data:", error);
      setIsDelerDataLoading(false);
    }
  };

  const fetchDealerDataWithinFisMonth = async () => {
    try {
      setIsDealerDataWithinFisMonthLoading(true);

      let countryCodes = "";

      if (Array.isArray(selectedCountries)) {
        countryCodes = selectedCountries.join(",");
      } else if (typeof selectedCountries === "string") {
        countryCodes = selectedCountries;
      } else {
        countryCodes = ""; // fallback if it's null or undefined
      }

      const response = await getDataWAuth(
        `${IB_GET_DEALER_REPORT_FISMONTH}?startFiscalMonth=${startFiscalMonth}-${defaultYear}&endFiscalMonth=${endFiscalMonth}-${defaultYear}&countryCodes=${countryCodes}`,
      );
      if (response.status === 200) {
        const dealerData = await response.data;
        const dealerMap = {};
        dealerData.dealerList.forEach((row) => {
          // For JDWIN countries, use dealerId+currency as key
          const isJDWIN =
            row.countryCode?.countryCode === "JD" || row.countryCode === "JD";
          const key = isJDWIN
            ? `${row.dealerId}_${row.currency}`
            : row.dealerId;
          if (!dealerMap[key]) {
            dealerMap[key] = {
              id: row.generatedId,
              country: row.countryCode?.countryName || "",
              dealerId: row.dealerId,
              dealerName: row.dealerName,
              currency: row.currency || "",
            };
          }
          dealerMap[key][row.fisMonthYear] = row.rate;
        });

        const fiscalMonthsList = response.data.fiscalDateDTOList.map(
          (date) => date.fismonthyear,
        );
        Object.values(dealerMap).forEach((dealer) => {
          fiscalMonthsList.forEach((monthYear) => {
            if (!(monthYear in dealer)) {
              dealer[monthYear] = null;
            }
          });
        });

        // After building cleanedDealerData, filter out rows with no rates
        const cleanedDealerData = Object.values(dealerMap)
          .map((dealer) => {
            const cleaned = { ...dealer };
            Object.keys(cleaned).forEach((key) => {
              if (
                /^\d{2}-\d{4}$/.test(key) && // matches "MM-YYYY"
                cleaned[key] === null
              ) {
                delete cleaned[key];
              }
            });
            return cleaned;
          })
          .filter((dealer) => {
            // Keep only if at least one fiscal month key has a non-null, non-undefined, non-empty value
            return Object.keys(dealer).some(
              (key) =>
                /^\d{2}-\d{4}$/.test(key) &&
                dealer[key] !== null &&
                dealer[key] !== undefined &&
                dealer[key] !== "",
            );
          });
        setDealerData(cleanedDealerData);

        const fiscalDateMap = dealerData.fiscalDateDTOList.reduce(
          (acc, date) => {
            acc[date.fismonthyear] = {
              startDate: date.startDate,
              endDate: date.endDate,
            };
            return acc;
          },
          {},
        );

        setFiscalDateMap(fiscalDateMap);

        const fiscalMonths = dealerData.fiscalDateDTOList.map(
          (date) => date.fismonthyear,
        );

        setFiscalMonths(fiscalMonths.sort());
        setIsDealerDataWithinFisMonthLoading(false);
      }
    } catch (error) {
      console.error("Error fetching bank rates data:", error);
    } finally {
      setIsViewReportActive(false);
      setIsDealerDataWithinFisMonthLoading(false);
    }
  };

  const handleYearChange = (e) => {
    setDefaultYear(e.target.value);
    setIsViewReportActive(true);
  };

  const handleStartDateChange = (e) => {
    setStartFiscalMonth(e.target.value);
    setIsViewReportActive(true);
  };

  const handleEndDateChange = (e) => {
    setEndFiscalMonth(e.target.value);
    setIsViewReportActive(true);
  };

  const handleReportCountryChange = (event) => {
    const options = event.target.options;
    const value = [];
    for (let i = 0, l = options.length; i < l; i++) {
      if (options[i].selected) {
        value.push(options[i].value);
      }
    }
    setSelectedCountries(value);
    setIsViewReportActive(true);
  };

  useEffect(() => {
    fetchDealerData();
  }, []);

  const gridToolbar = () => {
    return (
      <div>
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          {/* <GridToolbarExport printOptions={{ disableToolbarButton: true }} /> */}
          <Button
            color="inherit"
            size="small"
            startIcon={<FileDownloadOutlined style={{ color: "green" }} />}
            style={{
              boxShadow: "none",
              border: "none",
              background: "none",
              color: "green",
            }}
            onClick={handleExportXLSX}
          >
            Export
          </Button>
        </GridToolbarContainer>
      </div>
    );
  };

  const columns = [
    {
      field: "country",
      headerName: "Country",
      width: 90,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "dealerName",
      headerName: "Dealer Name",
      width: 250,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "currency",
      headerName: "Currency",
      width: 250,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "dealerId",
      headerName: "Customer Number",
      width: 130,
      headerAlign: "left",
      align: "left",
    },
    ...fiscalMonths.map((month, index) => ({
      field: month,
      headerName: formattedFiscalMonths[index] || "N/A",
      width: 180,
      headerAlign: "right",
      align: "right",
      renderHeader: (params) => {
        const header = params.colDef.headerName;
        const formattedHeader = header.replace(" to ", " to\n");

        return (
          <div
            style={{
              whiteSpace: "pre-line",
              textAlign: "right",
              fontWeight: "bold",
            }}
          >
            {formattedHeader}{" "}
          </div>
        );
      },

      valueFormatter: (params) => {
        return params ? Number(params).toFixed(2) + " %" : "-";
      },
    })),
  ];

  return (
    <div style={{ paddingTop: "20px", height: "100%", width: "100%" }}>
      {startFiscalMonth && endFiscalMonth && (
        <IBFilters
          dealerReport={true}
          defaultYear={defaultYear}
          startFiscalMonth={startFiscalMonth}
          endFiscalMonth={endFiscalMonth}
          dropdownOptions={dropdownOptions}
          reportCountryList={countryOptions}
          selectedReportCountry={selectedCountries}
          fiscalDateDTOList={fiscalMonths}
          handleYearChange={handleYearChange}
          handleStartDateChange={handleStartDateChange}
          handleEndDateChange={handleEndDateChange}
          handleReportCountryChange={handleReportCountryChange}
          fetchBankRatesByFiscalMonth={fetchDealerDataWithinFisMonth}
          isViewReportActive={isViewReportActive}
        />
      )}
      <div
        style={{
          marginTop: "25px",
          borderRadius: "10px",
          backgroundColor: "white",
          height: "88%",
        }}
      >
        <DataGrid
          loading={isDelerDataLoading || isDealerDataWithinFisMonthLoading}
          autoHeight
          rows={dealerData}
          columns={columns}
          pageSize={5}
          initialState={{
            sorting: {
              sortModel: [
                { field: "country", sort: "asc" },
                { field: "dealerId", sort: "asc" },
              ],
            },
          }}
          slots={{
            toolbar: gridToolbar,
          }}
          sx={{
            "& .MuiDataGrid-filler": {
              backgroundColor: "white",
            },

            "& .MuiDataGrid-columnHeader--sortable": {
              backgroundColor: "#fff",
            },
          }}
        />
      </div>
    </div>
  );
};

export default DealerRateAllocationReport;
